import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  useTheme,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Purpose, purposeService } from '../../services/purpose-service';
import { departmentService, Department } from '../../services/department-service';
import AddPurposePanel from './AddPurposePanel';
import EditPurposePanel from './EditPurposePanel';

// Query parameters interface for fetching purposes
interface PurposeQueryParams {
  filters?: {
    isPublished?: boolean;
    departmentId?: number;
  };
  pagination?: {
    page: number;
    pageSize: number;
  };
  sortBy?: 'a-z' | 'z-a' | 'newest' | 'oldest' | 'A-Z' | 'Z-A' | 'Newest' | 'Oldest';
}

const Purposes: React.FC = () => {
  const theme = useTheme();
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [purposes, setPurposes] = useState<Purpose[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [editingPurpose, setEditingPurpose] = useState<any | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    isPublished: 'ALL',
    departmentId: 'ALL'
  });
  const [tempFilters, setTempFilters] = useState({
    isPublished: 'ALL',
    departmentId: 'ALL'
  });

  useEffect(() => {
    loadDepartments();
    loadPurposes();
  }, [page, rowsPerPage, sortBy, filters]);

  const loadDepartments = async () => {
    try {
      const deps = await departmentService.getDepartments();
      setDepartments(deps);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadPurposes = async () => {
    try {
      setLoading(true);
      const queryParams: PurposeQueryParams = {
        filters: {
          isPublished: filters.isPublished === 'ALL' ? undefined : filters.isPublished === 'PUBLISHED',
          departmentId: filters.departmentId === 'ALL' ? undefined : parseInt(filters.departmentId)
        },
        sortBy: sortBy.toLowerCase() as 'a-z' | 'z-a' | 'newest' | 'oldest',
        pagination: {
          page: page + 1,
          pageSize: rowsPerPage
        }
      };

      const response = await purposeService.getPurposes(queryParams);
      setPurposes(response.data);
      setTotalCount(response.total);
    } catch (error) {
      console.error('Error fetching purposes:', error);
      setPurposes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id: number) => {
    try {
      await purposeService.deletePurpose(id);
      setPurposes(prev => prev.filter(purpose => purpose.purposeId !== id));
    } catch (error) {
      console.error('Error deleting purpose:', error);
    }
  };

  const handleStatusChange = async (id: number, newStatus: boolean) => {
    try {
      await purposeService.updatePurpose(id, { isActive: newStatus });
      setPurposes(prev => prev.map(p => 
        p.purposeId === id ? { ...p, isActive: newStatus } : p
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handlePublish = async (id: number) => {
    try {
      const updatedPurpose = await purposeService.publishPurpose(id);
      setPurposes(prev => prev.map(p => 
        p.purposeId === id ? updatedPurpose : p
      ));
    } catch (error) {
      console.error('Error publishing purpose:', error);
    }
  };

  const handleConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const handleConfirmAction = () => {
    confirmDialog.onConfirm();
    handleCloseConfirmDialog();
  };

  const handleEditClick = (purpose: any) => {
    setEditingPurpose(purpose);
    setIsEditPanelOpen(true);
  };

  const handleEditClose = () => {
    setIsEditPanelOpen(false);
    setEditingPurpose(null);
  };

  const handleFilterChange = (field: keyof typeof tempFilters, value: string) => {
    setTempFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = async () => {
    setFilters(tempFilters);
    setIsFilterModalOpen(false);
  };

  const handleCancelFilters = () => {
    setTempFilters(filters); // Reset to current filters
    setIsFilterModalOpen(false);
  };

  const handleOpenFilterModal = () => {
    setTempFilters(filters); // Initialize with current filters
    setIsFilterModalOpen(true);
  };

  const hasActiveFilters = () => {
    return filters.isPublished !== 'ALL' || filters.departmentId !== 'ALL';
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        color: theme.palette.text.primary
      }}>
        Loading purposes...
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ 
        mb: theme.custom.titleSpacing.marginBottom, 
        color: theme.palette.text.primary 
      }}>
        Purposes
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <IconButton
            onClick={() => loadPurposes()}
            sx={{ color: theme.palette.text.primary }}
            disabled={loading}
          >
            <RefreshIcon
              sx={{
                transition: 'transform 0.3s',
                animation: loading ? 'spin 1s linear infinite' : undefined
              }}
            />
          </IconButton>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            size="small"
            sx={{ width: 150 }}
          >
            <MenuItem value="A-Z">A-Z</MenuItem>
            <MenuItem value="Z-A">Z-A</MenuItem>
            <MenuItem value="newest">Newest</MenuItem>
            <MenuItem value="oldest">Oldest</MenuItem>
          </Select>
          <Tooltip title="Filters">
            <IconButton 
              onClick={handleOpenFilterModal}
              sx={{ 
                color: theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                },
                position: 'relative',
                '&::after': hasActiveFilters() ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: theme.palette.primary.main,
                  border: `2px solid ${theme.palette.background.paper}`,
                } : {}
              }}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsAddPanelOpen(true)}
            sx={{ 
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { 
                bgcolor: theme.palette.primary.dark 
              } 
            }}
          >
            ADD NEW
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: theme.palette.text.primary }}>Name</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Department</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary, textAlign: 'center' }}>Preferences</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary, textAlign: 'center' }}>Processors</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary, textAlign: 'center' }}>Data Elements</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary, textAlign: 'center' }}>Is Published</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Created Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purposes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ borderBottom: 'none' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 3,
                        color: theme.palette.text.secondary,
                        textAlign: 'center'
                      }}
                    >
                      There are no purposes to display. Click the button below to create your first purpose
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setIsAddPanelOpen(true)}
                      sx={{ 
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': { 
                          bgcolor: theme.palette.primary.dark 
                        },
                        '& .MuiSvgIcon-root': {
                          color: theme.palette.primary.contrastText,
                        }
                      }}
                    >
                      ADD NEW
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              (Array.isArray(purposes) ? purposes : []).map((purpose) => (
                <TableRow
                  key={purpose.purposeId}
                  hover
                  sx={{
                    '&.MuiTableRow-root:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <TableCell 
                    sx={{ 
                      color: theme.palette.text.primary,
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.palette.primary.main,
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={() => handleEditClick(purpose)}
                  >
                    {purpose.name}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>{purpose.departmentName}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary, textAlign: 'center' }}>
                    {purpose.mappings?.preferencesCount || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary, textAlign: 'center' }}>
                    {purpose.mappings?.processorsCount || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary, textAlign: 'center' }}>
                    {purpose.mappings?.dataElementsCount || 0}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Chip 
                      label={purpose.isPublished ? 'Published' : 'Draft'} 
                      size="small"
                      color={purpose.isPublished ? 'success' : 'default'}
                      sx={{ 
                        '& .MuiChip-label': {
                          color: theme.palette.getContrastText(
                            purpose.isPublished 
                              ? theme.palette.success.main 
                              : theme.palette.grey[500]
                          ),
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>
                    {purpose.createdDate ? new Date(purpose.createdDate).toLocaleDateString() : 'N/A'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />

      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} sx={{ color: theme.palette.text.primary }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Modal */}
      <Dialog
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: theme.palette.text.secondary }}>Department</InputLabel>
              <Select
                value={tempFilters.departmentId}
                onChange={(e) => handleFilterChange('departmentId', e.target.value)}
                label="Department"
                sx={{
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="ALL" sx={{ color: theme.palette.text.primary }}>All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.departmentId} value={dept.departmentId.toString()} sx={{ color: theme.palette.text.primary }}>
                    {dept.departmentName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.primary, fontWeight: 500 }}>
                Published Status
              </Typography>
              <RadioGroup
                value={tempFilters.isPublished}
                onChange={(e) => handleFilterChange('isPublished', e.target.value)}
              >
                <FormControlLabel 
                  value="PUBLISHED" 
                  control={<Radio />} 
                  label="Published"
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel 
                  value="DRAFT" 
                  control={<Radio />} 
                  label="Draft"
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel 
                  value="ALL" 
                  control={<Radio />} 
                  label="Both"
                  sx={{ color: theme.palette.text.primary }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 2 }} />
          <DialogActions>
            <Button 
              onClick={handleCancelFilters}
              sx={{ color: theme.palette.text.primary }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleApplyFilters}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                },
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <AddPurposePanel 
        open={isAddPanelOpen} 
        onClose={() => setIsAddPanelOpen(false)}
        onSave={() => loadPurposes()}
      />

      <EditPurposePanel
        open={isEditPanelOpen}
        onClose={handleEditClose}
        purpose={editingPurpose}
        onSave={() => loadPurposes()}
      />
    </Box>
  );
};

export default Purposes; 